import { useMemo } from "react";
import cx from "classnames";
import { useSearchParams } from "react-router";

import { Commit, EmptystateSearchNoResultsColored } from "components/icons/generated";
import InfiniteScroll from "components/scroll/InfiniteScroll";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import EmptyState from "ds/components/EmptyState";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Typography from "ds/components/Typography";
import PageLoading from "components/loading/PageLoading";
import useSearchSpaceliftVcsRepoCommits from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsRepoCommits";

import SpaceliftVcsRepoCommitsItem from "./Item";
import { SPACELIFT_VCS_REPO_COMMITS_SEARCH_FILTERS_STORAGE_KEY } from "./constants";
import { groupSpaceliftVcsRepoCommitsByDate } from "./helpers";
import styles from "./styles.module.css";

type SpaceliftVcsRepoCommitsProps = {
  repoId: string;
};

const SpaceliftVcsRepoCommits = ({ repoId }: SpaceliftVcsRepoCommitsProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);

  const { commits, loading, hasMore, loadMore } = useSearchSpaceliftVcsRepoCommits({
    repoId,
    fullTextSearch: searchQuery,
  });

  const sections = useMemo(() => groupSpaceliftVcsRepoCommitsByDate(commits), [commits]);

  return (
    <>
      <PageInfoHeader title="Commits">
        <SearchInput
          placeholder="Search commits..."
          filtersOrderSettingsKey={SPACELIFT_VCS_REPO_COMMITS_SEARCH_FILTERS_STORAGE_KEY}
        />
      </PageInfoHeader>

      {loading && commits.length === 0 && <PageLoading />}

      {!loading && commits.length === 0 && !searchQuery && (
        <EmptyState title="No commits yet" caption="This repository has no commits." />
      )}

      {!loading && commits.length === 0 && searchQuery && (
        <EmptyState
          title="No results"
          caption="Check the spelling or try something less specific."
          icon={EmptystateSearchNoResultsColored}
        />
      )}

      {commits.length > 0 && (
        <Box direction="column" padding="x-large">
          <InfiniteScroll onScrollEnd={loadMore} hasMore={hasMore} loading={loading}>
            {sections.map((section, index) => (
              <Box
                key={section.label}
                direction="column"
                gap="medium"
                padding="0 0 x-large"
                relative
                className={cx(styles.section, index === sections.length - 1 && styles.lastSection)}
              >
                <Box direction="row" gap="medium" align="center">
                  <Icon src={Commit} color="secondary" />
                  <Typography variant="p-body2" tag="span" color="secondary">
                    {section.label}
                  </Typography>
                </Box>

                <Box className={styles.commitsSection} direction="column" gap="medium">
                  {section.commits.map((commit) => (
                    <SpaceliftVcsRepoCommitsItem key={commit.id} commit={commit} repoId={repoId} />
                  ))}
                </Box>
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
      )}
    </>
  );
};

export default SpaceliftVcsRepoCommits;
